import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import Bowser from "bowser";
import { useMutation } from "@tanstack/react-query";
import {
	AvailabilityForm,
	DetailsForm,
	FinanceForm,
} from "@/components/forms/consultationForm/parts";
import { appSettingsConfig } from "@/lib/constants";
import { LoadingSpinner, BulletSteps } from "@/components/ui";
import { validators } from "@/lib/security";

import {
	postZap,
	sendEmail,
	getCurrentTime,
	getUserIP,
	useSearchEngineDetector,
	addProcessedIP,
	isIPProcessed,
} from "@/lib/utils";
import {
	AvailabilityFormData,
	AvailabilityFormErrors,
} from "@/components/forms/consultationForm/parts/availability";
import { PhoneValidationData } from "@/components/forms/consultationForm/parts/phoneValidation";
import { AnimateChangeInHeight } from "@/components/animations/animateHeight";
import { AnimatePresence, motion } from "framer-motion";
import { useFormStateStore } from "@/components/context/useFormContext";
import clsx from "clsx";
import { FormSuccessHeading } from "@/components/ui/formSuccessHeader";

export interface FormData {
	callTimeStart: AvailabilityFormData["callTimeStart"];
	callTimeEnd: AvailabilityFormData["callTimeEnd"];
	callDays: AvailabilityFormData["callDays"];
	siteTitle: string;
	loanType: string;
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	phoneVerified: boolean;
	phoneStage: PhoneValidationData["phoneStage"];
	state: string;
	postcode: string;
	tc: boolean;
	message: string;
}

export interface FormErrors {
	callTimeStart: AvailabilityFormErrors["callTimeStart"];
	callTimeEnd: AvailabilityFormErrors["callTimeEnd"];
	callDays: AvailabilityFormErrors["callDays"];
	firstName: boolean;
	lastName: boolean;
	email: boolean;
	phone: boolean;
	phoneVerified: boolean;
	state: boolean;
	postcode: boolean;
	tc: boolean;
	message: boolean;
	loanType: boolean;
}

export const InitialFormState: FormData = {
	callTimeStart: "8:30 AM",
	callTimeEnd: "4:30 PM",
	callDays: "",
	siteTitle: "",
	loanType: "",
	firstName: "",
	lastName: "",
	email: "",
	phone: "",
	phoneVerified: false,
	phoneStage: "enterPhone",
	state: "",
	postcode: "",
	tc: false,
	message: "",
};

export const InitialFormErrors: FormErrors = {
	callTimeStart: false,
	callTimeEnd: false,
	callDays: false,
	firstName: false,
	lastName: false,
	email: false,
	phone: false,
	phoneVerified: false,
	state: false,
	postcode: false,
	tc: false,
	message: false,
	loanType: false,
};

function usePrevious<T>(value: T) {
	let ref = useRef(value);

	useEffect(() => {
		ref.current = value;
	}, [value]);

	return ref.current;
}

const variants = {
	initial: (isForwards: boolean) => {
		return {
			opacity: 0,
		};
	},
	animate: {
		opacity: 1,
	},
	exit: (isForwards: boolean) => {
		return {
			opacity: 0,
		};
	},
};

export function Mobile({ rootElement }: { rootElement: HTMLElement }) {
	const isNavDivSmall = true;
	const PHONE_PAGE = 1;
	const { stepProcess, setStepProcess, selectedIndex, setSelectedIndex } =
		useFormStateStore();
	const [stepsMaxIndex, setStepsMaxIndex] = useState(stepProcess.length - 1);
	const prevIndex = usePrevious(selectedIndex);
	const isForwards =
		prevIndex === undefined ? true : selectedIndex > prevIndex;
	// Form Data
	const [formData, setFormData] = useState<FormData>(InitialFormState);
	const [formErrors, setFormErrors] = useState<FormErrors>(InitialFormErrors);
	// Source Details
	const sourceUrl = window.location.href;
	const [userIP, setUserIP] = useState("");
	const [usingVPN, setUsingVPN] = useState(false);
	const [browserInfo, setBrowserInfo] = useState({});
	const detectedSearchEngine = useSearchEngineDetector();
	const [searchEngine, setSearchEngine] = useState<string>("Direct");
	const timer = useRef<NodeJS.Timeout | undefined>(undefined);
	const [tabHasFocus, setTabHasFocus] = useState(true);
	const headingRef = useRef<HTMLHeadingElement | null>(null);
	const [openSuccessModal, setOpenSuccessModal] = useState(false); // Open Success Modal
	const [enabledTC, setEnabledTC] = useState(formData.tc); // tc

	// Track each mutation's success states
	const submissionStatusRef = useRef({ mail: false, zap: false });

	const resetForm = useCallback(() => {
		setFormData(InitialFormState);
		setFormErrors(InitialFormErrors);
		setEnabledTC(InitialFormState.tc);
		// Reset tracking for next submission
		submissionStatusRef.current = { mail: false, zap: false };
	}, []);

	const checkBothSucceeded = () => {
		if (
			submissionStatusRef.current.mail &&
			submissionStatusRef.current.zap
		) {
			setOpenSuccessModal(true);
			resetForm();
		}
	};

	const handleSubmit = () => {
		sendMail.mutate();
		sendZap.mutate();

		resetForm();
	};

	// Availability Form
	const availFormData = useMemo(
		() => ({
			callTimeStart: formData.callTimeStart,
			callTimeEnd: formData.callTimeEnd,
			callDays: formData.callDays,
		}),
		[formData.callTimeStart, formData.callTimeEnd, formData.callDays]
	);

	const availFormErrors = useMemo(
		() => ({
			callTimeStart: formErrors.callTimeStart,
			callTimeEnd: formErrors.callTimeEnd,
			callDays: formErrors.callDays,
		}),
		[formErrors.callTimeStart, formErrors.callTimeEnd, formErrors.callDays]
	);

	// Finance Form
	const financeFormData = useMemo(
		() => ({
			loanType: formData.loanType,
		}),
		[formData.loanType]
	);
	const financeFormErrors = useMemo(
		() => ({
			loanType: formErrors.loanType,
		}),
		[formErrors.loanType]
	);

	// Details Form
	const detailsFormData = useMemo(
		() => ({
			firstName: formData.firstName,
			lastName: formData.lastName,
			email: formData.email,
			phone: formData.phone,
			phoneVerified: formData.phoneVerified,
			phoneStage: formData.phoneStage,
			state: formData.state,
			postcode: formData.postcode,
			tc: formData.tc,
			message: formData.message,
		}),
		[
			formData.firstName,
			formData.lastName,
			formData.email,
			formData.phone,
			formData.phoneVerified,
			formData.phoneStage,
			formData.state,
			formData.postcode,
			formData.tc,
			formData.message,
		]
	);
	const detailsFormErrors = useMemo(
		() => ({
			firstName: formErrors.firstName,
			lastName: formErrors.lastName,
			email: formErrors.email,
			phone: formErrors.phone,
			phoneVerified: formErrors.phoneVerified,
			state: formErrors.state,
			postcode: formErrors.postcode,
			tc: formErrors.tc,
			message: formErrors.message,
		}),
		[
			formErrors.firstName,
			formErrors.lastName,
			formErrors.email,
			formErrors.phone,
			formErrors.phoneVerified,
			formErrors.state,
			formErrors.postcode,
			formErrors.tc,
			formErrors.message,
		]
	);

	const validateForm = useCallback(() => {
		let newErrors = {};

		if (selectedIndex === 0) {
			newErrors = {
				callTimeStart: !formData.callTimeStart,
				callTimeEnd: !formData.callTimeEnd,
				callDays: !formData.callDays,
			};
		} else if (selectedIndex === 1) {
			newErrors = {
				loanType: !formData.loanType,
			};
		} else if (selectedIndex === 2) {
			// fixed duplicate
			newErrors = {
				firstName: !validators.name(formData.firstName),
				lastName: !validators.name(formData.lastName),
				email: formData.email
					? !validators.email(formData.email)
					: false,
				phone: !validators.phone(formData.phone),
				phoneVerified: !formData.phoneVerified,
				state: !validators.name(formData.state),
				postcode: !validators.postcode(formData.postcode),
				tc: !formData.tc,
				message: formData.message
					? !validators.textarea(formData.message)
					: false,
			};
		}

		setFormErrors((prevErrors) => ({ ...prevErrors, ...newErrors }));
		return newErrors;
	}, [selectedIndex, formData]);

	// Determining the form steps
	const getStepProcess = () => {
		setStepProcess("Consultation");
		setStepsMaxIndex(stepProcess.length - 1);
	};

	useEffect(() => {
		const rootSiteTitle =
			rootElement.getAttribute("data-title") ?? "Yes Home Loans";
		setFormData((prevData) => ({ ...prevData, siteTitle: rootSiteTitle }));
		getStepProcess();
	}, [rootElement, setFormData, stepProcess, stepsMaxIndex]);

	// Apply Color Theme Settings
	useEffect(() => {
		if (!rootElement) return;
		const siteTitle =
			rootElement?.getAttribute("data-title") ?? "Yes Home Loans";

		rootElement.style.setProperty(
			`--yF-theme-color`,
			rootElement.getAttribute(`themeColor`) ??
				appSettingsConfig[siteTitle].themeColor
		);
		rootElement.style.setProperty(
			`--yF-theme-h-text`,
			rootElement.getAttribute(`themeHeaderTxt`) ??
				appSettingsConfig[siteTitle].themeHeaderTxt
		);
		rootElement.style.setProperty(
			`--yF-hover-color`,
			rootElement.getAttribute(`hoverColor`) ??
				appSettingsConfig[siteTitle].hoverColor
		);
		rootElement.style.setProperty(
			`--yF-bullet-bg`,
			rootElement.getAttribute(`bulletBG`) ??
				appSettingsConfig[siteTitle].bulletBG
		);
	}, [rootElement, appSettingsConfig]);

	// Source Details For Zapier: IP, VPN, Browser Info, Search Engine
	const fetchIPData = useCallback(async () => {
		try {
			const { userIP, usingVPN } = await getUserIP();
			setUserIP(userIP);
			setUsingVPN(usingVPN);
		} catch (error) {
			console.error("Failed to fetch user IP data:", error);
		}
	}, []);

	useEffect(() => {
		if (formData.phoneVerified) {
			fetchIPData();
		}
		const browser = Bowser.parse(window.navigator.userAgent);
		const info = `${browser.browser.name} ${browser.browser.version}, ${browser.os.name}, ${browser.platform.type}`;
		setBrowserInfo(info);
		setSearchEngine(detectedSearchEngine);
	}, [formData.phoneVerified]);

	// Detect if tab is inactive
	useEffect(() => {
		const handleFocus = () => {
			setTabHasFocus(true);
			if (timer.current !== undefined) {
				clearTimeout(timer.current);
			}
		};

		const handleBlur = () => {
			setTabHasFocus(false);
			timer.current = setTimeout(() => {
				// Handle the tab being inactive for 3 minutes here
				if (formData.phoneVerified && !isIPProcessed(userIP)) {
					sendPreZap.mutate();
					sendPreMail.mutate();
					addProcessedIP(userIP);
				}
			}, 180000); // 180000 milliseconds = 3 minutes
		};

		window.addEventListener("focus", handleFocus);
		window.addEventListener("blur", handleBlur);

		// Clean up
		return () => {
			window.removeEventListener("focus", handleFocus);
			window.removeEventListener("blur", handleBlur);
			if (timer.current !== undefined) {
				clearTimeout(timer.current);
			}
		};
	}, [formData.phoneVerified, userIP]);

	// Special Case: Skipping Loan Details Page and Additional Information Page
	const shouldSkipLoanSteps = null;

	const getPageIncrement = useCallback(
		(navButton: string, currentIndex: number): number => {
			if (!shouldSkipLoanSteps) return 1;

			// Livestock Loan - Skip Loan Details Page and Additional Information Page.
			switch (navButton) {
				case "prev":
					return currentIndex === PHONE_PAGE ? 1 : 1;
				case "next":
					return currentIndex === PHONE_PAGE ? 3 : 1;
				default:
					return 1;
			}
		},
		[shouldSkipLoanSteps]
	);

	// Prevents Next button from being spammed causing skipping multiple pages.
	const [isPageLoading, setIsPageLoading] = useState(false);

	const next = useCallback(() => {
		if (isPageLoading) return;

		const validationErrors = validateForm();

		const hasErrors = Object.values(validationErrors).some(
			(value) => value === true
		);
		if (hasErrors) return;

		if (selectedIndex < stepsMaxIndex) {
			setIsPageLoading(true);
			const increment = getPageIncrement("next", selectedIndex);
			setSelectedIndex(selectedIndex + increment);
			setTimeout(() => setIsPageLoading(false), 500); // Add delay to give time for components to load.
		}

		if (selectedIndex === stepsMaxIndex - 1) {
			if (!hasErrors) {
				handleSubmit();
			}
		}
	}, [
		isPageLoading,
		shouldSkipLoanSteps,
		selectedIndex,
		stepsMaxIndex,
		getPageIncrement,
		formData,
	]);

	const prev = useCallback(() => {
		const decrement = getPageIncrement("prev", selectedIndex);
		setSelectedIndex(selectedIndex - decrement);
	}, [selectedIndex, stepsMaxIndex, shouldSkipLoanSteps, getPageIncrement]);

	// Detect when tab is reloaded or closed
	useEffect(() => {
		const handleBeforeUnload = (event: any) => {
			if (formData.phoneVerified && !isIPProcessed(userIP)) {
				sendPreZap.mutate();
				sendPreMail.mutate();
				addProcessedIP(userIP);
			}
			resetForm();
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		window.addEventListener("pagehide", handleBeforeUnload);

		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
			window.removeEventListener("pagehide", handleBeforeUnload);
		};
	}, [formData.phoneVerified, userIP]);

	const sendPreZap = useMutation({
		mutationKey: [formData],
		mutationFn: async () => {
			const zapPost = {
				...formData,
				dateSubmitted: getCurrentTime(),
				formName: `${formData.siteTitle} Partial Consultation Form`,
				tc: undefined,
				phoneVerified: undefined,
				// Source Details
				source: sourceUrl,
				source_id: 18,
				userIP: userIP,
				isUsingVPN: usingVPN.toString(),
				userAgent: browserInfo,
				searchEngine: searchEngine,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) =>
						value !== null && value !== undefined && value !== ""
				)
			);

			const data = await postZap(cleanedZapPost);
			return data;
		},
		onSuccess() {},
	});

	const sendPreMail = useMutation({
		mutationKey: [formData],
		mutationFn: async () => {
			const zapPost = {
				...formData,
				dateSubmitted: getCurrentTime(),
				formName: `${formData.siteTitle} Partial Consultation Form`,
				tc: undefined,
				phoneVerified: undefined,
				// Source Details
				source: sourceUrl,
				source_id: 18,
				userIP: userIP,
				isUsingVPN: usingVPN.toString(),
				userAgent: browserInfo,
				searchEngine: searchEngine,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) =>
						value !== null && value !== undefined && value !== ""
				)
			);

			const data = await sendEmail(cleanedZapPost);
			return data;
		},
		onSuccess() {},
	});

	const sendZap = useMutation({
		mutationKey: [formData],
		mutationFn: async () => {
			const zapPost = {
				...formData,
				dateSubmitted: getCurrentTime(),
				formName: `${formData.siteTitle} Consultation Form`,
				tc: undefined,
				phoneVerified: undefined,
				// Source Details
				source: sourceUrl,
				source_id: 18,
				userIP: userIP,
				isUsingVPN: usingVPN.toString(),
				userAgent: browserInfo,
				searchEngine: searchEngine,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) =>
						value !== null && value !== undefined && value !== ""
				)
			);

			const data = await postZap(cleanedZapPost);
			return data;
		},
		onSuccess() {
			submissionStatusRef.current.zap = true;
			checkBothSucceeded();
		},
	});

	const sendMail = useMutation({
		mutationKey: [formData],
		mutationFn: async () => {
			const zapPost = {
				...formData,
				dateSubmitted: getCurrentTime(),
				formName: `${formData.siteTitle} Consultation Form`,
				tc: undefined,
				phoneVerified: undefined,
				// Source Details
				source: sourceUrl,
				source_id: 18,
				userIP: userIP,
				isUsingVPN: usingVPN.toString(),
				userAgent: browserInfo,
				searchEngine: searchEngine,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) =>
						value !== null && value !== undefined && value !== ""
				)
			);

			const data = await sendEmail(cleanedZapPost);
			return data;
		},
		onSuccess() {
			submissionStatusRef.current.mail = true;
			checkBothSucceeded();
		},
	});

	// Track if any mutation is loading
	const isSubmitting = sendMail.isPending || sendZap.isPending;

	return (
		<div className="yF-bg-white">
			<AnimateChangeInHeight>
				<form className="yF-flex yF-flex-col yF-space-y-4 yF-relative">
					{/* Loading Overlay */}
					{isSubmitting && (
						<div className="yF-absolute yF-inset-0 yF-bg-white yF-bg-opacity-75 yF-z-50 yF-flex yF-items-center yF-justify-center yF-rounded-lg">
							<div className="yF-flex yF-flex-col yF-items-center yF-gap-3">
								<LoadingSpinner />
								<p className="yF-text-gray-600 yF-font-medium">
									Submitting your consultation form . . .
								</p>
							</div>
						</div>
					)}

					<AnimatePresence
						initial={false}
						mode="wait"
						custom={isForwards}
					>
						{selectedIndex === 0 && (
							<motion.div
								key={0}
								initial="initial"
								animate="animate"
								exit="exit"
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								variants={variants}
								custom={isForwards}
								className="yF-space-y-4"
							>
								<div className="yF-flex-1 yF-px-6 yF-py-6 yF-shadow-md yF-bg-white">
									<AvailabilityForm
										formData={availFormData}
										setFormData={setFormData}
										formErrors={availFormErrors}
										setFormErrors={setFormErrors}
									/>
								</div>
							</motion.div>
						)}

						{selectedIndex === 1 && (
							<motion.div
								key={1}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
							>
								<div className="yF-flex-1 yF-px-6 yF-py-6 yF-border-gray-300 yF-shadow-md yF-bg-white">
									<FinanceForm
										formData={financeFormData}
										setFormData={setFormData}
										formErrors={financeFormErrors}
										setFormErrors={setFormErrors}
									/>
								</div>
							</motion.div>
						)}

						{selectedIndex === 2 && (
							<motion.div
								key={2}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
							>
								<div className="yF-flex yF-flex-col yF-w-full yF-px-6 yF-py-6 yF-border-gray-300 yF-shadow-md yF-bg-white">
									<DetailsForm
										formData={detailsFormData}
										setFormData={setFormData}
										formErrors={detailsFormErrors}
										setFormErrors={setFormErrors}
										enabledTCstatus={enabledTC}
										setEnabledTCstatus={setEnabledTC}
									/>
								</div>
							</motion.div>
						)}

						{/* Success Page */}
						{selectedIndex === stepsMaxIndex && (
							<motion.div
								key={selectedIndex}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
								className="space-y-4"
							>
								<FormSuccessHeading
									onClose={() => setSelectedIndex(0)}
									title="Sucessfully Submitted"
									description="Thank you for your consultation request. We will get back to you shortly."
									headingRef={headingRef}
								/>
							</motion.div>
						)}
					</AnimatePresence>
				</form>
			</AnimateChangeInHeight>

			{/* Navigation */}
			{selectedIndex < stepsMaxIndex ? (
				<div
					className={clsx(
						isNavDivSmall ? "yF-flex-wrap" : "yF-flex-nowrap",
						"yF-flex yF-items-center yF-justify-center yF-border-t yF-border-gray-200 yF-bg-white yF-mt-2 yF-py-3 yF-px-4"
					)}
				>
					<BulletSteps
						steps={stepProcess}
						selectedIndex={selectedIndex}
						navRef={isNavDivSmall}
					/>
					<div
						className={clsx(
							selectedIndex === stepsMaxIndex
								? "yF-hidden"
								: "yF-flex yF-flex-1 yF-justify-end yF-gap-x-2"
						)}
					>
						<button
							className={clsx(
								selectedIndex === 0
									? "yF-hidden"
									: isNavDivSmall
									? "yF-w-full"
									: "yF-w-auto",
								"yF-bg-white hover:yF-bg-gray-50 yF-relative yF-inline-flex yF-items-center yF-rounded-md yF-justify-center yF-px-3 yF-py-2 yF-text-md yF-font-semibold yF-text-gray-900 yF-ring-1 yF-ring-inset yF-ring-gray-300 focus-visible:yF-outline-offset-0"
							)}
							onClick={() => prev()}
						>
							Previous
						</button>
						<button
							className={clsx(
								selectedIndex === 2 && !enabledTC
									? isNavDivSmall
										? "yF-bg-gray-100 yF-text-gray-400 yF-ring-gray-100 yF-w-full"
										: "yF-bg-gray-100 yF-text-gray-400 yF-ring-gray-100 yF-w-auto"
									: isNavDivSmall
									? "yF-bg-[--yF-theme-color] hover:yF-bg-[--yF-hover-color] yF-w-full yF-text-white"
									: "yF-bg-[--yF-theme-color] hover:yF-bg-[--yF-hover-color] yF-w-auto yF-text-white",
								"yF-relative yF-inline-flex yF-items-center yF-rounded-md yF-justify-center yF-px-3 yF-py-2 yF-text-md yF-font-semibold yF-ring-1 yF-ring-inset yF-ring-[--yF-theme-color] focus-visible:yF-outline-offset-0"
							)}
							onClick={() => {
								next();
							}}
						>
							{selectedIndex === stepsMaxIndex - 1
								? "Submit"
								: "Next"}
						</button>
					</div>
				</div>
			) : (
				<></>
			)}
		</div>
	);
}
